/* eslint-disable no-prototype-builtins */
import { TFunction } from 'i18next';
import { withTranslation, WithTranslation } from 'next-i18next';
import React, { ReactNode } from 'react';

import { RegisterOptions, useFormContext } from 'react-hook-form';
import { VisuallyHiddenElement } from '~/components/common/layout/styles';
import { buildErrorMsgInterpolationOptions } from '~/utils/helpers';
import FormGroup from '../form-group';
import HintExpander from '../hint-expander';
import HintText from '../hint-text';
import InlineError from '../inline-error';
import Label from '../label';
import { ClearButton, FormTextField, TextFieldWithButtonContainer } from './styles';

export interface TextInputProps extends WithTranslation {
    autocomplete?: string;
    defaultValue?: string | null;
    warnings?: boolean | string;
    errors?: boolean | string;
    errorTitle: string;
    hiddenLabel?: boolean;
    hintExpander?: boolean;
    hintExpanderContent?: string | ReactNode;
    hintExpanderLabel?: string | null;
    hintExpanderContentType?: 'image' | 'text' | 'html';
    hintId?: string;
    hintText?: string | null;
    hintText2?: string | null;
    id: string;
    inputSize?: string;
    labelSize?: 'default' | 'large';
    name: string;
    numberFormatDefaultValue?: string;
    onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    clearInput?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    t: TFunction;
    text?: string | null;
    type?: string;
    registerOptions?: RegisterOptions;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    clearButton?: boolean;
    list?: string;
    showRequirementFlag?: boolean;
    showHintExpanderOnError?: boolean;
    className?: string;
    children?: ReactNode;
    ariaLabelledby?: string;
    trimInput?: boolean;
}

const TextInput = ({
    autocomplete,
    defaultValue,
    warnings,
    errors,
    errorTitle,
    hiddenLabel,
    hintExpander = false,
    hintExpanderContent,
    hintExpanderLabel,
    hintExpanderContentType = 'image',
    hintId,
    hintText,
    hintText2,
    id,
    inputSize,
    labelSize = 'default',
    name,
    numberFormatDefaultValue,
    onBlur,
    onChange,
    clearInput,
    t,
    text,
    type = 'text',
    registerOptions,
    required,
    disabled = false,
    readOnly = false,
    clearButton = false,
    list,
    showRequirementFlag = true,
    showHintExpanderOnError = false,
    className,
    children,
    ariaLabelledby,
    trimInput = false,
}: TextInputProps) => {
    const { register, setValue } = useFormContext();

    const hintIds = [`hintText_${hintId}`, hintText2 && `hintText2_${hintId}`].filter(Boolean);
    return (
        <FormGroup className={className} errors={!!errors} id={`formgroup-${id}`}>
            {text && (
                <Label
                    id={id}
                    addRequiredFlag={required}
                    size={labelSize}
                    readonly={hiddenLabel}
                    showRequirementFlag={showRequirementFlag}
                >
                    <>
                        {text}
                        {hintExpander && (
                            <VisuallyHiddenElement as="span">
                                {t('common:form.hintExpanderAccessibilityText.moreInfo')} {text}{' '}
                                {t('common:form.hintExpanderAccessibilityText.inNextElement')}
                            </VisuallyHiddenElement>
                        )}
                    </>
                </Label>
            )}
            {hintText && <HintText id={`hintText_${hintId}`}>{hintText}</HintText>}
            {hintText2 && <HintText id={`hintText2_${hintId}`}>{hintText2}</HintText>}
            {errors && (
                <InlineError id={`${id}-error`}>
                    {`errorCodes.${errors}` === t(`errors:errorCodes.${errors}`)
                        ? errors
                        : t(
                              `errors:errorCodes.${String(errors)?.split(',')[0]}`,
                              buildErrorMsgInterpolationOptions(errors?.toString())
                          )}
                </InlineError>
            )}
            {warnings && (
                <InlineError id={`${id}-warning`} type="warning">
                    {`errorCodes.${warnings}` === t(`errors:errorCodes.${warnings}`)
                        ? warnings
                        : t(
                              `errors:errorCodes.${warnings}`,
                              buildErrorMsgInterpolationOptions(warnings?.toString())
                          )}
                </InlineError>
            )}
            <TextFieldWithButtonContainer width={inputSize}>
                <FormTextField
                    aria-describedby={
                        [...hintIds, ...(errors ? [`${id}-error`] : [])]
                            .filter(Boolean)
                            .join(' ') || undefined
                    }
                    aria-invalid={!!errors}
                    type={type}
                    id={id}
                    list={list}
                    disabled={disabled}
                    readOnly={readOnly}
                    defaultValue={defaultValue || numberFormatDefaultValue}
                    data-errortitle={errorTitle}
                    width={inputSize}
                    autoComplete={autocomplete || 'off'}
                    $errors={!!errors}
                    $warnings={!!warnings}
                    aria-labelledby={errors ? name : ariaLabelledby}
                    {...register(name, {
                        ...registerOptions,
                        onChange,
                        onBlur: (e: any) => {
                            if (trimInput && typeof e.target.value === 'string') {
                                const trimmedValue = e.target.value.trim();

                                if (trimmedValue !== e.target.value) {
                                    e.target.value = trimmedValue;

                                    setValue(name, trimmedValue, {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                        shouldTouch: true,
                                    });
                                }
                            }

                            onBlur?.(e);
                            registerOptions?.onBlur?.(e);
                        },
                    })}
                />
                {clearButton && (
                    <ClearButton onClick={e => clearInput && clearInput(e)}>X</ClearButton>
                )}
                {children && children}
            </TextFieldWithButtonContainer>
            {hintExpander && (
                <HintExpander
                    id={id}
                    label={hintExpanderLabel ?? ''}
                    content={hintExpanderContent}
                    type={hintExpanderContentType}
                    isExpanded={showHintExpanderOnError && !!errors}
                />
            )}
        </FormGroup>
    );
};

export default withTranslation(['errors', 'common'])(TextInput);
